import { Box } from '@mui/material';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useAppSelector } from '@/hooks/redux';
import { useAddressCityList } from '@/hooks/useAddressCityList';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { AddressForm as AddressFormType } from '@/types/form';

import { getDataTestId } from '@/utils/browser';
import {
  isTrinidadOrTobagoCitizen,
  isTrinidadOrTobagoUser,
} from '@/utils/user';

import { AddressCityField } from './AddressCityField';
import { AddressFieldsProps } from './AddressFields';
import {
  AddressCountrySelectField,
  AddressLine1Field,
  AddressStateField,
  AddressZipField,
} from './StandardAddressFormFields';

const DefaultAddressFields = ({
  isPending,
  popularCountryOptions,
}: AddressFieldsProps) => {
  const { control, setValue, clearErrors } = useFormContext<AddressFormType>();

  const user = useAppSelector(selectLoggedInUser);
  const selectedCountry = useWatch({ control, name: 'country' });
  const line1 = useWatch({ control, name: 'line1' });

  const shouldDisableCountrySelect =
    isTrinidadOrTobagoUser(user) && isTrinidadOrTobagoCitizen(user);

  const { resolveCityFromPlace } = useAddressCityList();

  useEffect(() => {
    if (line1 && typeof line1 === 'object') {
      const effectiveCountry = shouldDisableCountrySelect
        ? selectedCountry
        : line1.country;

      setValue('city', resolveCityFromPlace(line1.city, effectiveCountry));
      setValue('state', line1.state || '');
      setValue('zip', line1.zip || '');
      if (!shouldDisableCountrySelect) {
        setValue('country', line1.country || '');
      }
      setValue('line1', line1.line1 || '');
      clearErrors(['city', 'state', 'zip', 'country', 'line1']);
    }
  }, [
    line1,
    setValue,
    clearErrors,
    resolveCityFromPlace,
    shouldDisableCountrySelect,
    selectedCountry,
  ]);

  const onCountryChange = () => setValue('city', '');

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={theme => theme.spacing(3.5)}
      {...getDataTestId('default-address-fields')}
    >
      <AddressLine1Field name="line1" control={control} isPending={isPending} />

      <AddressCityField
        name="city"
        control={control}
        isPending={isPending}
        userTerritoryCode={user?.territoryCode}
        selectedCountry={selectedCountry}
      />

      <AddressStateField name="state" control={control} isPending={isPending} />

      <AddressZipField name="zip" control={control} isPending={isPending} />

      <AddressCountrySelectField
        name="country"
        control={control}
        isPending={isPending}
        popularCountryOptions={popularCountryOptions}
        disabled={isPending || shouldDisableCountrySelect}
        onSelectChange={onCountryChange}
      />
    </Box>
  );
};

export default React.memo(DefaultAddressFields);
